import { CommonHeader } from '@/ui/Table'
import { CellContext, createColumnHelper, Getter, HeaderContext, Row } from '@tanstack/react-table'
import { useMemo } from 'react'
import {
    BreakpointType,
    ControlSheetAnnotation,
    ControlSheetNote,
    ControlSheetRow,
    ControlSheetTrip,
} from '../types/types'
import { TableHeaderWithTooltip } from '../ui/TableHeaderWithTooltip'

import styles from './ControlSheetCells.module.scss'
import clsx from 'clsx'
import { BreakpointCell } from './cells/BreakpointCell'
import { LayoverMealReliefCell } from './cells/LayoverMealReliefCell'
import { LayoverShiftEndCell } from './cells/LayoverShiftEndCell'
import { tripSorter } from '../helpers/controlSheetSorter'
import { isTimeSameOrAfter } from '../helpers/breakpointTimeUtils'
import { standardizeTime } from '../helpers/standardizeTime'

import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { BreakPointDescDTO, ControlSheetDTO } from '@/types/controlsheet'
import { DutyCell } from './cells/DutyCell'
import { Tooltip } from '@/ui/Tooltip'
import { runTripTooltip } from './cells/RunTripTooltip'
import { TripCell } from './cells/TripCell'
import { AnnotationsPermission } from '@/pages/ControlSheet/ui/ControlSheetPage'

const columnHelper = createColumnHelper<ControlSheetRow>()

// HEADERS
const tripHeader = (context: HeaderContext<ControlSheetRow, { tripNumber: string; garage: string | undefined }>) => (
    <CommonHeader label="Trip" searchPlaceholder="Type to search for trip" {...context} />
)

const dutyHeader = (
    context: HeaderContext<ControlSheetRow, { trip: ControlSheetTrip; run: string; route: string }>
) => <CommonHeader label="Duty" searchPlaceholder="Type to search for duty" {...context} />

const breakpointHeader = (bp: BreakPointDescDTO) => () => (
    <TableHeaderWithTooltip title={bp.name}>{bp.name}</TableHeaderWithTooltip>
)

// CELLS
const tripCell = (
    info: CellContext<
        ControlSheetRow,
        {
            trip?: ControlSheetTrip
            tripRoute?: string
            isNextTrip?: boolean
            withLink?: boolean
            annotationsPermission: AnnotationsPermission
            navigateToTrip?: (trip: string) => void
        }
    >
) => (
    <TripCell
        trip={info.getValue().trip}
        tripRoute={info.getValue().tripRoute}
        isNextTrip={info.getValue().isNextTrip}
        navigateToTrip={info.getValue().navigateToTrip}
        annotationsPermission={info.getValue().annotationsPermission}
        withLink={info.getValue().withLink}
    />
)

const runCell = ({ row, table }: CellContext<ControlSheetRow, string | number>) => {
    // Show run number only if it's the first row of the run
    const sortedRows = table.getFilteredRowModel().rows
    const currentIndex = sortedRows.findIndex((r) => r.id === row.id)

    let showRow = false

    if (currentIndex === 0) {
        showRow = true
    } else {
        const previousRun = sortedRows[currentIndex - 1].getValue('run')
        showRow = previousRun !== row.original.run
    }

    const tripWithDepartGarage = (row: ControlSheetRow) => {
        if (row.outbound && row.outbound.departGarage) {
            return row.outbound
        }
        if (row.inbound && row.inbound.departGarage) {
            return row.inbound
        }
        return undefined
    }

    const child = <div className={clsx(styles.cell, styles.run)}>{showRow && row.original.run}</div>

    const trip = tripWithDepartGarage(row.original)
    if (trip) {
        const tooltip = runTripTooltip(trip)
        return (
            <Tooltip tooltip={tooltip} placement="bottom" trigger="hover">
                {child}
            </Tooltip>
        )
    }

    return child
}

const breakpointCell =
    (
        isOutbound: boolean,
        index: number,
        route: string,
        mealRelief: number,
        annotationsPermission: AnnotationsPermission,
        refetchControlSheet: (
            options?: RefetchOptions | undefined
        ) => Promise<QueryObserverResult<ControlSheetDTO[], Error>>,
        addTempNote: (note: ControlSheetNote) => void,
        deleteTempNote: (noteId: string) => void,
        addTempAnnotation: (annotation: ControlSheetAnnotation) => void
    ) =>
    ({ getValue, row }: { getValue: Getter<BreakpointType>; row: Row<ControlSheetRow> }) => {
        return (
            <BreakpointCell
                isOutbound={isOutbound}
                index={index}
                route={route}
                row={row.original}
                value={getValue()}
                mealRelief={mealRelief}
                refetchControlSheet={refetchControlSheet}
                addTempNote={addTempNote}
                deleteTempNote={deleteTempNote}
                addTempAnnotation={addTempAnnotation}
                annotationsPermission={annotationsPermission}
            />
        )
    }

const dutyCell =
    (
        route: string,
        annotationsPermission: AnnotationsPermission,
        refetchControlSheet: (
            options?: RefetchOptions | undefined
        ) => Promise<QueryObserverResult<ControlSheetDTO[], Error>>,
        addTempNote: (note: ControlSheetNote) => void,
        deleteTempNote: (noteId: string) => void,
        addTempAnnotation: (annotation: ControlSheetAnnotation) => void
    ) =>
    (info: CellContext<ControlSheetRow, { trip: ControlSheetTrip; run: string; route: string }>) => {
        return (
            <DutyCell
                trip={info.getValue().trip}
                run={info.getValue().run}
                route={route}
                refetchControlSheet={refetchControlSheet}
                addTempNote={addTempNote}
                deleteTempNote={deleteTempNote}
                addTempAnnotation={addTempAnnotation}
                annotationsPermission={annotationsPermission}
            />
        )
    }

// META
const runMeta = {
    width: 'var(--size-64)',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parseColumnValue: (value: any) => value,
    formatFilterValues: (values: string[]) => values.sort(),
}

const tripMeta = {
    width: 'calc(var(--size-64) + var(--size-32) + var(--size-16))',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parseColumnValue: (value: any) => String(value.trip?.trip),
    formatFilterValues: (values: string[]) =>
        values
            .filter((a) => a !== 'undefined')
            .sort((a, b) => {
                const aNum = a ? parseInt(a.trim().replace(/\D/g, ''), 10) : 0
                const bNum = b ? parseInt(b.trim().replace(/\D/g, ''), 10) : 0
                return aNum - bNum
            }),
}

const dutyMeta = {
    width: 'calc(var(--size-64) + var(--size-32))',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parseColumnValue: (value: any) => String(value.trip?.duty),
    formatFilterValues: (values: string[]) =>
        values
            .filter((a) => a !== 'undefined')
            .sort((a, b) => {
                const aNum = parseInt(a.trim().replace(/\D/g, ''), 10)
                const bNum = parseInt(b.trim().replace(/\D/g, ''), 10)
                return aNum - bNum
            }),
}

const breakpointMeta = (isOutbound: boolean, bp: BreakPointDescDTO) => {
    return {
        maxWidth: 'calc(var(--size-64) + var(--size-20))',
        width: 'calc(var(--size-64) + var(--size-20))',
        getColSpan: (row: ControlSheetRow) => {
            const bps = isOutbound ? row.outbound?.breakpoints : row.inbound?.breakpoints
            return bps
                ? (bps.filter((it) => it !== 'SKIP_CELL').find((it) => it?.stopId === bp.stopId)?.colspan ?? 1)
                : 1
        },
        getType: (row: ControlSheetRow) => {
            const bps = isOutbound ? row.outbound?.breakpoints : row.inbound?.breakpoints
            const breakpoint = bps?.filter((it) => it !== 'SKIP_CELL').find((it) => it?.stopId === bp.stopId)
            return (breakpoint && breakpoint.type) ?? 'normal'
        },
        getText: (row: ControlSheetRow) => {
            const bps = isOutbound ? row.outbound?.breakpoints : row.inbound?.breakpoints
            return bps?.filter((it) => it !== 'SKIP_CELL').find((it) => it?.stopId === bp.stopId)?.text
        },
    }
}

const layoverMeta = {
    maxWidth: 'calc(var(--size-64) + var(--size-20))',
    width: 'calc(var(--size-64) + var(--size-20))',
}

const layoverCell =
    (
        isOutbound: boolean,
        route: string,
        mealRelief: number,
        annotationsPermission: AnnotationsPermission,
        refetchControlSheet: (
            options?: RefetchOptions | undefined
        ) => Promise<QueryObserverResult<ControlSheetDTO[], Error>>,
        addTempNote: (note: ControlSheetNote) => void,
        deleteTempNote: (noteId: string) => void,
        addTempAnnotation: (annotation: ControlSheetAnnotation) => void
    ) =>
    ({ getValue, row }: { getValue: Getter<number | undefined>; row: Row<ControlSheetRow> }) => {
        const trip = isOutbound ? row.original.outbound : row.original.inbound
        if (!trip?.trip) {
            return <></>
        }

        if (trip.isTakeoverRow) {
            return <></>
        }

        // Check if this is R/RX trip where break starts after arrival (meal relief in layover)
        const isROrRX = trip.code === 'R' || trip.code === 'RX'
        const breakStartAfterArrival =
            trip.breakStart &&
            trip.arriveStandTime &&
            isTimeSameOrAfter(standardizeTime(trip.breakStart), standardizeTime(trip.arriveStandTime))

        if (isROrRX && breakStartAfterArrival) {
            return (
                <LayoverMealReliefCell
                    trip={trip}
                    run={row.original.run}
                    route={route}
                    mealRelief={mealRelief}
                    annotationsPermission={annotationsPermission}
                    addTempNote={addTempNote}
                    deleteTempNote={deleteTempNote}
                    addTempAnnotation={addTempAnnotation}
                />
            )
        }

        // Check if this is F/FX trip where shift ends after arrival (shift end in layover)
        const isFOrFX = trip.code === 'F' || trip.code === 'FX'
        const shiftEndAfterArrival =
            trip.shiftEnd &&
            trip.arriveStandTime &&
            isTimeSameOrAfter(standardizeTime(trip.shiftEnd), standardizeTime(trip.arriveStandTime))

        if (isFOrFX && shiftEndAfterArrival) {
            return (
                <LayoverShiftEndCell
                    trip={trip}
                    run={row.original.run}
                    route={route}
                    annotationsPermission={annotationsPermission}
                    refetchControlSheet={refetchControlSheet}
                    addTempNote={addTempNote}
                    deleteTempNote={deleteTempNote}
                    addTempAnnotation={addTempAnnotation}
                />
            )
        }

        return <div className={clsx(styles.cell, styles.normalTime, styles.lastNormalTime)}>{getValue()}</div>
    }

const controlSheetColumns = (
    route: string | undefined,
    breakpoints: { outbound: BreakPointDescDTO[]; inbound: BreakPointDescDTO[] },
    mealRelief: number,
    annotationsPermission: AnnotationsPermission,
    inboundTripNumForLowestOutbound: number,
    refetchControlSheet: (
        options?: RefetchOptions | undefined
    ) => Promise<QueryObserverResult<ControlSheetDTO[], Error>>,
    addTempNote: (note: ControlSheetNote) => void,
    deleteTempNote: (noteId: string) => void,
    addTempAnnotation: (annotation: ControlSheetAnnotation) => void,
    navigateToTrip: (trip: string) => void
) => [
    ////// OUTBOUND
    columnHelper.accessor((row) => row.run, {
        id: 'run',
        header: (context) => <CommonHeader label="Run" searchPlaceholder="Type to search for run" {...context} />,
        cell: runCell,
        meta: runMeta,
        filterFn: (row, _columnId, filterValues) => {
            const runNumber = row.original.run
            const filtersUpper = filterValues.map((val: string | undefined) =>
                typeof val === 'string' ? (val as string).toUpperCase() : val
            )
            return filtersUpper.includes(runNumber.toUpperCase())
        },
    }),

    columnHelper.accessor(
        (row) => ({
            trip: row.outbound,
            annotationsPermission: annotationsPermission,
        }),
        {
            id: 'outbound-trip',
            header: tripHeader,
            filterFn: (row, _columnId, filterValues) => {
                const tripNumber = row.original.outbound?.trip
                return filterValues.includes(tripNumber)
            },
            sortingFn: tripSorter(inboundTripNumForLowestOutbound),
            cell: tripCell,
            meta: tripMeta,
        }
    ),

    columnHelper.accessor(
        (row) => ({
            trip: row.outbound,
            run: row.run,
        }),
        {
            id: 'outbound-duty',
            header: dutyHeader,
            filterFn: (row, _columnId, filterValues) => {
                const outboundDuty = row.original.outbound?.duty
                const inboundDuty = row.original.inbound?.duty
                return filterValues.includes(outboundDuty) || filterValues.includes(inboundDuty)
            },
            cell: dutyCell(
                route ?? '',
                annotationsPermission,
                refetchControlSheet,
                addTempNote,
                deleteTempNote,
                addTempAnnotation
            ),
            meta: dutyMeta,
        }
    ),

    ...breakpoints.outbound.map((bp, index) => {
        return columnHelper.accessor((row) => (row.outbound?.breakpoints || [])[index], {
            id: `outboundBreakpoint-${index}`,
            header: breakpointHeader(bp),
            cell: breakpointCell(
                true,
                index,
                route ?? '',
                mealRelief,
                annotationsPermission,
                refetchControlSheet,
                addTempNote,
                deleteTempNote,
                addTempAnnotation
            ),
            meta: breakpointMeta(true, bp),
        })
    }),

    columnHelper.accessor((row) => row.outbound?.layover, {
        id: 'outboundLayover',
        header: () => <TableHeaderWithTooltip>Layover</TableHeaderWithTooltip>,
        cell: layoverCell(
            true,
            route ?? '',
            mealRelief,
            annotationsPermission,
            refetchControlSheet,
            addTempNote,
            deleteTempNote,
            addTempAnnotation
        ),
        meta: layoverMeta,
    }),

    ////// DIVIDER

    columnHelper.accessor(() => null, {
        id: 'divider',
        header: () => <></>,
        cell: () => <div className={styles.divider}></div>,
        meta: {
            width: '1px',
            maxWidth: '1px',
        },
    }),

    ////// INBOUND

    columnHelper.accessor(
        (row) => {
            if (!row.inbound && (row.outbound?.code === 'R' || row.outbound?.code === 'RX')) {
                return {
                    trip: row.outbound,
                    tripRoute: route,
                    isNextTrip: true,
                    withLink: true,
                    navigateToTrip: navigateToTrip,
                    annotationsPermission: annotationsPermission,
                }
            }
            return {
                trip: row.inbound,
                annotationsPermission: annotationsPermission,
            }
        },
        {
            id: 'inboundTrip',
            header: tripHeader,
            filterFn: (row, _columnId, filterValues) => {
                const tripNumber = row.original.inbound?.trip
                return filterValues.includes(tripNumber)
            },
            sortingFn: tripSorter(inboundTripNumForLowestOutbound),
            cell: tripCell,
            meta: tripMeta,
        }
    ),

    columnHelper.accessor(
        (row) => ({
            trip: row.inbound,
            run: row.run,
        }),
        {
            id: 'inboundDuty',
            header: dutyHeader,
            filterFn: (row, _columnId, filterValues) => {
                const outboundDuty = row.original.outbound?.duty
                const inboundDuty = row.original.inbound?.duty
                return filterValues.includes(outboundDuty) || filterValues.includes(inboundDuty)
            },
            cell: dutyCell(
                route ?? '',
                annotationsPermission,
                refetchControlSheet,
                addTempNote,
                deleteTempNote,
                addTempAnnotation
            ),
            meta: dutyMeta,
        }
    ),

    ...breakpoints.inbound.map((bp, index) => {
        return columnHelper.accessor((row) => (row.inbound?.breakpoints || [])[index], {
            id: `inboundBreakpoint-${index}`,
            header: breakpointHeader(bp),
            cell: breakpointCell(
                false,
                index,
                route ?? '',
                mealRelief,
                annotationsPermission,
                refetchControlSheet,
                addTempNote,
                deleteTempNote,
                addTempAnnotation
            ),
            meta: breakpointMeta(false, bp),
        })
    }),
    columnHelper.accessor((row) => row.inbound?.layover, {
        id: 'inboundLayover',
        header: () => <TableHeaderWithTooltip>Layover</TableHeaderWithTooltip>,
        cell: layoverCell(
            false,
            route ?? '',
            mealRelief,
            annotationsPermission,
            refetchControlSheet,
            addTempNote,
            deleteTempNote,
            addTempAnnotation
        ),
        meta: layoverMeta,
    }),

    columnHelper.accessor(
        (row) => ({
            trip: row.inbound,
            tripRoute: route,
            isNextTrip: true,
            withLink: row.inbound?.code === 'R' || row.inbound?.code === 'RX',
            annotationsPermission: annotationsPermission,
            navigateToTrip: navigateToTrip,
        }),
        {
            id: 'nextTrip',
            header: () => <span>Next trip</span>,
            cell: tripCell,
            meta: tripMeta,
        }
    ),
]

export const useControlSheetColumns = (
    route: string | undefined,
    breakpoints: { outbound: BreakPointDescDTO[]; inbound: BreakPointDescDTO[] },
    isDataLoading: boolean,
    mealRelief: number,
    annotationsPermission: AnnotationsPermission,
    inboundTripNumForLowestOutbound: number,
    refetchControlSheet: (
        options?: RefetchOptions | undefined
    ) => Promise<QueryObserverResult<ControlSheetDTO[], Error>>,
    addTempNote: (note: ControlSheetNote) => void,
    deleteTempNote: (noteId: string) => void,
    addTempAnnotation: (annotation: ControlSheetAnnotation) => void,
    navigateToTrip: (trip: string) => void
) => {
    return useMemo(
        () =>
            !isDataLoading && breakpoints
                ? controlSheetColumns(
                      route,
                      breakpoints,
                      mealRelief,
                      annotationsPermission,
                      inboundTripNumForLowestOutbound,
                      refetchControlSheet,
                      addTempNote,
                      deleteTempNote,
                      addTempAnnotation,
                      navigateToTrip
                  )
                : [],
        [
            isDataLoading,
            breakpoints,
            route,
            mealRelief,
            annotationsPermission,
            inboundTripNumForLowestOutbound,
            refetchControlSheet,
            addTempNote,
            deleteTempNote,
            addTempAnnotation,
            navigateToTrip,
        ]
    )
}
